import { TrackExtras, TrackResponse, ClientRequest } from './types';

const TIMEOUT_MS = 5000;
const FAILURE: TrackResponse = { success: false };
const SUCCESS: TrackResponse = { success: true };

/**
 * The Pintail client.
 */
export class Client {
  /**
   * The base URL for the Pintail API.
   */
  private baseUrl = 'https://event.pintail.io/v2';

  /**
   * The constructor.
   *
   * @param baseUrl The base URL for the Pintail API. Defaults to 'https://event.pintail.io/v2'.
   */
  public constructor(baseUrl?: string) {
    if (baseUrl) {
      this.baseUrl = baseUrl;
    }
  }

  /**
   * Track an event.
   *
   * This method never throws. Any transport, serialization, timeout, or
   * server-side failure is surfaced via `console.warn` and reported back as
   * `{ success: false }`.
   *
   * In the browser, the request is preferentially dispatched via
   * `navigator.sendBeacon`, which survives page unload and is rarely blocked
   * by corporate proxies or extensions. When sendBeacon is unavailable or
   * refuses the payload, the call falls back to `fetch` with a 5-second
   * timeout and a CORS-safelisted `text/plain` content type so the request
   * does not trigger an `OPTIONS` preflight.
   *
   * @param codename The codename of the event to track.
   * @param extras The extras to track. Defaults to an empty object.
   * @param req The client request. Defaults to null.
   * @returns The response from the Pintail API. When `sendBeacon` is used,
   *   `{ success: true }` reflects a successful enqueue rather than a server
   *   acknowledgement.
   */
  public track = async (
    codename: string,
    extras: TrackExtras = {},
    req: ClientRequest | null = null,
  ): Promise<TrackResponse> => {
    if (typeof codename !== 'string' || codename.length === 0) {
      console.warn('Pintail: track() called without a codename; skipping.');
      return FAILURE;
    }

    let body: string;
    try {
      body = JSON.stringify({ extras, req });
    } catch (err) {
      console.warn('Pintail: failed to serialize event payload:', err);
      return FAILURE;
    }

    const url = `${this.baseUrl}/${encodeURIComponent(codename)}`;

    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function' &&
      typeof Blob === 'function'
    ) {
      try {
        const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
        if (navigator.sendBeacon(url, blob)) {
          return SUCCESS;
        }
      } catch (err) {
        console.warn('Pintail: sendBeacon dispatch failed, falling back:', err);
      }
    }

    if (typeof fetch !== 'function') {
      console.warn('Pintail: no transport available (fetch missing).');
      return FAILURE;
    }

    const controller =
      typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => controller.abort(), TIMEOUT_MS)
      : null;

    try {
      const response = await fetch(url, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        keepalive: true,
        credentials: 'omit',
        mode: 'cors',
        cache: 'no-store',
        redirect: 'follow',
        referrerPolicy: 'no-referrer',
        signal: controller ? controller.signal : undefined,
      });

      if (!response.ok) {
        console.warn(
          `Pintail: track() received ${response.status} ${response.statusText}`,
        );
        return FAILURE;
      }

      try {
        return (await response.json()) as TrackResponse;
      } catch {
        return SUCCESS;
      }
    } catch (err) {
      const aborted = err instanceof Error && err.name === 'AbortError';
      if (aborted) {
        console.warn(`Pintail: track() aborted after ${TIMEOUT_MS}ms.`);
      } else {
        console.warn('Pintail: track() failed:', err);
      }
      return FAILURE;
    } finally {
      if (timer !== null) {
        clearTimeout(timer);
      }
    }
  };
}
